import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound, UserX, UserCheck } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PageHeader, TH } from "@/components/common";
import { MIN_PASSWORD } from "@/components/ChangePasswordDialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, fmtDate } from "@/lib/api";

const ROLES = { admin: "Administrateur", operateur: "Opérateur" };

function CreateUserDialog({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "operateur" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ name: "", email: "", password: "", role: "operateur" }); }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < MIN_PASSWORD) return toast.error(`Mot de passe : au moins ${MIN_PASSWORD} caractères.`);
    setBusy(true);
    try { await api.post("/users", f); toast.success("Utilisateur créé"); onSaved(); onOpenChange(false); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="create-user-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Nouvel utilisateur</DialogTitle><DialogDescription>Communiquez le mot de passe temporaire à l'utilisateur ; il pourra le changer.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Nom</Label><Input required value={f.name} onChange={set("name")} className="h-12" data-testid="user-name-input" /></div>
          <div className="space-y-1.5"><Label>E-mail</Label><Input type="email" required value={f.email} onChange={set("email")} className="h-12" data-testid="user-email-input" /></div>
          <div className="space-y-1.5"><Label>Mot de passe temporaire</Label><Input required value={f.password} onChange={set("password")} className="h-12 font-mono" data-testid="user-password-input" /></div>
          <div className="space-y-1.5"><Label>Rôle</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger className="h-12" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`user-role-option-${k}`}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2"><Button type="button" variant="outline" className="h-12" onClick={() => onOpenChange(false)}>Annuler</Button><Button type="submit" className="h-12" disabled={busy} data-testid="user-save-button">Créer</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ target, onClose }) {
  const [pwd, setPwd] = useState("");
  useEffect(() => setPwd(""), [target]);
  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < MIN_PASSWORD) return toast.error(`Mot de passe : au moins ${MIN_PASSWORD} caractères.`);
    try { await api.post(`/users/${target.id}/reset-password`, { password: pwd }); toast.success(`Mot de passe de ${target.name} réinitialisé`); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Réinitialiser le mot de passe</DialogTitle><DialogDescription>{target?.name} — ses sessions en cours seront déconnectées.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Input required value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={`Nouveau mot de passe (${MIN_PASSWORD} car. min.)`} className="h-12 font-mono" data-testid="reset-password-input" />
          <DialogFooter className="gap-2"><Button type="button" variant="outline" className="h-12" onClick={onClose}>Annuler</Button><Button type="submit" className="h-12" data-testid="reset-password-submit">Réinitialiser</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? `${u.name} désactivé` : `${u.name} réactivé`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Utilisateurs" subtitle="Seuls les administrateurs peuvent créer et gérer les comptes." testid="users-title">
        <Button className="h-12 gap-2" onClick={() => setCreating(true)} data-testid="users-new-button"><UserPlus className="h-4 w-4" /> Nouvel utilisateur</Button>
      </PageHeader>
      <div className="overflow-x-auto rounded-md border bg-white shadow-sm">
        <Table data-testid="users-table">
          <TableHeader><TableRow><TableHead className={TH}>Nom</TableHead><TableHead className={TH}>E-mail</TableHead><TableHead className={TH}>Rôle</TableHead><TableHead className={TH}>Statut</TableHead><TableHead className={TH}>Créé le</TableHead><TableHead className={`${TH} text-right`}>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`} className={u.active ? "" : "opacity-60"}>
                <TableCell className="font-semibold">{u.name}{u.id === me?.id && <span className="ml-2 text-xs text-slate-400">(vous)</span>}</TableCell>
                <TableCell className="font-mono text-sm">{u.email}</TableCell>
                <TableCell>{ROLES[u.role] || u.role}</TableCell>
                <TableCell><span data-testid={`user-status-${u.email}`} className={`rounded border px-2 py-0.5 text-xs font-semibold ${u.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-600"}`}>{u.active ? "Actif" : "Désactivé"}</span></TableCell>
                <TableCell className="font-mono text-xs">{u.created_at ? fmtDate(u.created_at) : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" className="h-11 gap-2" onClick={() => setResetting(u)} data-testid={`user-reset-${u.email}`}><KeyRound className="h-4 w-4" /> Mot de passe</Button>
                    {u.id !== me?.id && (
                      <Button variant="ghost" className={`h-11 gap-2 ${u.active ? "text-rose-600" : "text-emerald-700"}`} onClick={() => toggle(u)} data-testid={`user-toggle-${u.email}`}>
                        {u.active ? <><UserX className="h-4 w-4" /> Désactiver</> : <><UserCheck className="h-4 w-4" /> Réactiver</>}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <CreateUserDialog open={creating} onOpenChange={setCreating} onSaved={load} />
      <ResetPasswordDialog target={resetting} onClose={() => setResetting(null)} />
    </div>
  );
}
